import {
	type ApiKey,
	postApiKeysIdRevoke,
	postApiKeysRevokeAll
} from '@repo/api-portal-sdk/gen'
import { Button } from '@repo/ui/button'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@repo/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/tabs'
import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationNext,
	PaginationPrevious
} from '@repo/ui/pagination'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/lib/query-keys'
import {
	dashboardApiKeysQueryOptions,
	dashboardUsageQueryOptions
} from '../-lib/dashboard-queries'
import { ApiKeyCreationDialogContent } from './api-key-creation-dialog'
import {
	DashboardQueryError,
	DashboardQueryLoading
} from './dashboard-query-state'

type RevokeTarget = ApiKey | 'all'

export function ApiKeysCard(props: { onMessage: (message: string) => void }) {
	const keysQuery = useQuery(dashboardApiKeysQueryOptions())
	const usageQuery = useQuery(dashboardUsageQueryOptions())
	const queryClient = useQueryClient()
	const { t } = useTranslation()
	const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null)
	const creationDisabled =
		usageQuery.isSuccess && usageQuery.data.apiKeys.remaining === 0

	async function refreshApiKeys() {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.apiKeys.all()
			}),
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.billing.usage()
			})
		])
	}

	const revokeMutation = useMutation({
		mutationFn: async (target: RevokeTarget) => {
			if (target === 'all') {
				await postApiKeysRevokeAll()
				return target
			}

			await postApiKeysIdRevoke({ path: { id: target.id } })
			return target
		},
		onSuccess: async (target) => {
			setRevokeTarget(null)
			await refreshApiKeys()
			props.onMessage(
				target === 'all'
					? t('common:dashboard.keys_revoked')
					: t('common:dashboard.key_revoked')
			)
		},
		onError: () => {
			props.onMessage(t('common:dashboard.key_revoke_failed'))
		}
	})

	return (
		<>
			<Dialog>
				<section className='portal-card dashboard-data-card'>
					<div className='portal-card__header'>
						<div>
							<h2 className='portal-card__title'>
								{t('common:dashboard.api_keys')}
							</h2>
							<p className='portal-meta'>
								{t('common:dashboard.api_keys_description')}
							</p>
						</div>
						{keysQuery.isSuccess ? (
							<DialogTrigger
								render={
									<Button
										disabled={creationDisabled}
										size='sm'
									/>
								}
							>
								<Plus aria-hidden='true' />
								{t('common:dashboard.generate_key')}
							</DialogTrigger>
						) : null}
					</div>
					{keysQuery.isPending ? (
						<DashboardQueryLoading
							label={t('common:dashboard.api_keys_loading')}
						/>
					) : null}
					{keysQuery.isError ? (
						<DashboardQueryError
							description={t(
								'common:dashboard.api_keys_error_description'
							)}
							isRetrying={keysQuery.isFetching}
							onRetry={() => keysQuery.refetch()}
							retryLabel={t(
								keysQuery.isFetching
									? 'common:dashboard.retrying'
									: 'common:dashboard.retry'
							)}
							title={t('common:dashboard.api_keys_error_title')}
						/>
					) : null}
					{keysQuery.isSuccess ? (
						<ApiKeyViews
							emptyCreationTrigger={
								<DialogTrigger
									render={
										<Button disabled={creationDisabled} />
									}
								>
									<Plus aria-hidden='true' />
									{t('common:dashboard.create_key')}
								</DialogTrigger>
							}
							onRevoke={setRevokeTarget}
							statusCounts={keysQuery.data.meta.statusCounts}
						/>
					) : null}
				</section>
				<ApiKeyCreationDialogContent />
			</Dialog>
			<Dialog
				onOpenChange={(open) => {
					if (!(open || revokeMutation.isPending)) {
						setRevokeTarget(null)
					}
				}}
				open={revokeTarget !== null}
			>
				<DialogContent showCloseButton={false}>
					<DialogHeader>
						<DialogTitle>
							{t('common:dashboard.revoke_title')}
						</DialogTitle>
						<DialogDescription>
							{revokeTarget === 'all'
								? t('common:dashboard.revoke_all_description')
								: t('common:dashboard.revoke_description')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<DialogClose
							className='portal-button portal-button--secondary'
							disabled={revokeMutation.isPending}
							type='button'
						>
							{t('common:dashboard.cancel')}
						</DialogClose>
						<button
							className='portal-button portal-button--danger'
							disabled={
								revokeMutation.isPending ||
								revokeTarget === null
							}
							onClick={() => {
								if (revokeTarget) {
									revokeMutation.mutate(revokeTarget)
								}
							}}
							type='button'
						>
							{revokeMutation.isPending
								? t('common:dashboard.revoking')
								: t('common:dashboard.revoke')}
						</button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}

function ApiKeyViews(props: {
	emptyCreationTrigger: ReactNode
	onRevoke: (key: ApiKey | 'all') => void
	statusCounts: Record<ApiKey['status'], number>
}) {
	const { t } = useTranslation()
	const [status, setStatus] = useState<ApiKey['status']>('active')
	const [page, setPage] = useState(1)
	const keysQuery = useQuery(dashboardApiKeysQueryOptions({ page, status }))

	function selectStatus(value: string) {
		if (value === 'active' || value === 'expired' || value === 'revoked') {
			setStatus(value)
			setPage(1)
		}
	}

	return (
		<Tabs className='gap-3.5' onValueChange={selectStatus} value={status}>
			<TabsList
				aria-label={t('common:dashboard.api_key_views')}
				className='flex h-auto w-full justify-start gap-1 overflow-x-auto border-(--portal-border) border-b bg-transparent p-0 pb-2'
				variant='line'
			>
				<ApiKeyTab
					count={props.statusCounts.active}
					label={t('common:dashboard.active')}
					value='active'
				/>
				<ApiKeyTab
					count={props.statusCounts.revoked}
					label={t('common:dashboard.revoked')}
					value='revoked'
				/>
				<ApiKeyTab
					count={props.statusCounts.expired}
					label={t('common:dashboard.expired')}
					value='expired'
				/>
			</TabsList>
			<TabsContent className='min-w-0' value='active'>
				{keysQuery.isPending ? (
					<DashboardQueryLoading
						label={t('common:dashboard.api_keys_loading')}
					/>
				) : null}
				{keysQuery.isError ? (
					<DashboardQueryError
						description={t(
							'common:dashboard.api_keys_error_description'
						)}
						isRetrying={keysQuery.isFetching}
						onRetry={() => keysQuery.refetch()}
						retryLabel={t('common:dashboard.retry')}
						title={t('common:dashboard.api_keys_error_title')}
					/>
				) : null}
				{keysQuery.isSuccess && keysQuery.data.data.length === 0 ? (
					<div className='api-key-empty flex min-h-[11.875rem] flex-col items-center justify-center gap-3 pt-[1.125rem] pb-1 text-center'>
						<div className='dashboard-empty-state__icon'>
							<KeyRound aria-hidden='true' />
						</div>
						<div>
							<h3>
								{t('common:dashboard.no_active_keys_title')}
							</h3>
							<p>
								{t(
									'common:dashboard.no_active_keys_description'
								)}
							</p>
						</div>
						{props.emptyCreationTrigger}
					</div>
				) : null}
				{keysQuery.isSuccess && keysQuery.data.data.length > 0 ? (
					<>
						<ApiKeyList
							keys={keysQuery.data.data}
							onRevoke={props.onRevoke}
						/>
						{props.statusCounts.active >= 2 ? (
							<button
								className='portal-button portal-button--danger portal-button--small api-key-revoke-all'
								onClick={() => props.onRevoke('all')}
								type='button'
							>
								<RotateCcw aria-hidden='true' />
								{t('common:dashboard.revoke_all')}
							</button>
						) : null}
						<ApiKeyPagination
							meta={keysQuery.data.meta}
							onPageChange={setPage}
						/>
					</>
				) : null}
			</TabsContent>
			<TabsContent className='min-w-0' value='revoked'>
				{keysQuery.isSuccess ? (
					<ApiKeyHistory
						description={t(
							'common:dashboard.no_revoked_keys_description'
						)}
						keys={keysQuery.data.data}
						meta={keysQuery.data.meta}
						onPageChange={setPage}
						title={t('common:dashboard.no_revoked_keys_title')}
					/>
				) : null}
			</TabsContent>
			<TabsContent className='min-w-0' value='expired'>
				{keysQuery.isSuccess ? (
					<ApiKeyHistory
						description={t(
							'common:dashboard.no_expired_keys_description'
						)}
						keys={keysQuery.data.data}
						meta={keysQuery.data.meta}
						onPageChange={setPage}
						title={t('common:dashboard.no_expired_keys_title')}
					/>
				) : null}
			</TabsContent>
		</Tabs>
	)
}

function ApiKeyTab(props: { count: number; label: string; value: string }) {
	return (
		<TabsTrigger
			className='group h-9 shrink-0 gap-[7px] rounded-lg px-2.5 text-(--portal-muted) data-[active]:bg-(--portal-surface-raised) data-[active]:text-(--portal-fg)'
			value={props.value}
		>
			{props.label}
			<span className='inline-grid h-[22px] min-w-[22px] place-items-center rounded-full bg-(--portal-bg-raised) px-1.5 font-mono text-(--portal-muted) text-[10px] group-data-[active]:text-(--portal-fg)'>
				{props.count}
			</span>
		</TabsTrigger>
	)
}

function ApiKeyHistory(props: {
	description: string
	keys: ApiKey[]
	meta: { page: number; totalPages: number }
	onPageChange: (page: number) => void
	title: string
}) {
	if (props.keys.length > 0) {
		return (
			<>
				<ApiKeyList keys={props.keys} />
				<ApiKeyPagination
					meta={props.meta}
					onPageChange={props.onPageChange}
				/>
			</>
		)
	}

	return (
		<div className='api-key-empty flex min-h-[11.125rem] flex-col items-center justify-center gap-3 pt-[1.125rem] pb-1 text-center'>
			<div className='dashboard-empty-state__icon'>
				<KeyRound aria-hidden='true' />
			</div>
			<div>
				<h3>{props.title}</h3>
				<p>{props.description}</p>
			</div>
		</div>
	)
}

function ApiKeyPagination(props: {
	meta: { page: number; totalPages: number }
	onPageChange: (page: number) => void
}) {
	const { t } = useTranslation()
	if (props.meta.totalPages <= 1) {
		return null
	}

	return (
		<Pagination className='api-key-pagination'>
			<PaginationContent>
				<PaginationItem>
					<PaginationPrevious
						disabled={props.meta.page === 1}
						onClick={() => props.onPageChange(props.meta.page - 1)}
						text={t('common:dashboard.previous_page')}
					/>
				</PaginationItem>
				<PaginationItem>
					<span className='api-key-pagination__label'>
						{t('common:dashboard.api_key_page', {
							page: props.meta.page,
							total: props.meta.totalPages
						})}
					</span>
				</PaginationItem>
				<PaginationItem>
					<PaginationNext
						disabled={props.meta.page === props.meta.totalPages}
						onClick={() => props.onPageChange(props.meta.page + 1)}
						text={t('common:dashboard.next_page')}
					/>
				</PaginationItem>
			</PaginationContent>
		</Pagination>
	)
}

function ApiKeyList(props: {
	keys: ApiKey[]
	onRevoke?: (key: ApiKey) => void
}) {
	const { t } = useTranslation()

	return (
		<div className='grid gap-2.5'>
			{props.keys.map((key) => (
				<div
					className='flex flex-col items-stretch justify-between gap-3 rounded-[10px] border border-(--portal-border) bg-(--portal-bg-raised) p-3 sm:flex-row sm:items-center'
					key={key.id}
				>
					<div>
						<span
							className={
								key.status === 'active'
									? 'status-pill status-pill--ok'
									: 'status-pill status-pill--warning'
							}
						>
							{t(`common:dashboard.key_status_${key.status}`)}
						</span>
						<div className='font-bold text-sm'>{key.label}</div>
						<span className='portal-meta mt-1 block'>
							{key.prefix}
						</span>
					</div>
					{props.onRevoke ? (
						<div className='flex gap-1.5'>
							<button
								aria-label={t(
									'common:dashboard.revoke_key_label',
									{ label: key.label }
								)}
								className='icon-button'
								onClick={() => props.onRevoke?.(key)}
								type='button'
							>
								<Trash2 aria-hidden='true' />
							</button>
						</div>
					) : null}
				</div>
			))}
		</div>
	)
}
